import { useMemo } from 'react';
import chroma from 'chroma-js';
import { useTheme } from '@withjoy/joykit/theme';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';

export type WeatherReportColorSet = {
  accent: string;
  tokens: {
    cardBg: string;
    border: string;
    textPrimary: string;
    textSecondary: string;
    icon: string;
    pillBg: string;
    pillBorder: string;
  };
  gradients: {
    hero: (summary?: string) => string;
    precip: string;
    humidity: string;
    wind: string;
    uv: string;
    temperature: (range: { min: number; max: number }) => string; // full-width gradient used with clipping
  };
};

const makeTrackGradient = (accent: string): string => {
  const base = chroma(accent);
  const light = base.brighten(1.5).alpha(0.25).css();
  const mid = base.alpha(0.6).css();
  const dark = base.darken(0.3).alpha(0.9).css();
  return `linear-gradient(90deg, ${light} 0%, ${mid} 50%, ${dark} 100%)`;
};

const TEMPERATURE_DOMAIN = [-15, -5, 5, 15, 23, 30, 37, 45];
const TEMPERATURE_COLORS = ['#38bdf8', '#22d3ee', '#34d399', '#a3e635', '#fde047', '#f97316', '#ef4444', '#b91c1c'];
const TEMPERATURE_SCALE = chroma.scale(TEMPERATURE_COLORS).domain(TEMPERATURE_DOMAIN).mode('lab');
const FALLBACK_TEMPERATURE_GRADIENT = 'linear-gradient(90deg, #38bdf8 0%, #ef4444 100%)';

// Builds a gradient whose stops are aligned to the actual temperature span so that
// hot forecasts start with warm hues and cold ones stay cool-toned.
export const buildTemperatureGradient = ({ min, max }: { min: number; max: number }): string => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return FALLBACK_TEMPERATURE_GRADIENT;
  }

  const start = Math.min(min, max);
  const end = Math.max(min, max);
  const span = end - start;

  if (span < 0.0001) {
    const singleColor = TEMPERATURE_SCALE(start).hex();
    return `linear-gradient(90deg, ${singleColor} 0%, ${singleColor} 100%)`;
  }

  const domainStops = TEMPERATURE_DOMAIN.filter(value => value > start && value < end);
  const stops = [start, ...domainStops, end];

  const gradientStops = stops.map(value => {
    const percent = ((value - start) / span) * 100;
    const color = TEMPERATURE_SCALE(value).hex();
    return `${color} ${percent.toFixed(2)}%`;
  });

  return `linear-gradient(90deg, ${gradientStops.join(', ')})`;
};

const makeWindGradient = (accent: string): string => {
  // Slightly cooler variant of accent for wind
  const base = chroma(accent).set('hsl.h', '+20');
  const light = base.brighten(1.3).alpha(0.3).css();
  const mid = base.alpha(0.9).css();
  const dark = base.darken(0.3).alpha(1).css();
  return `linear-gradient(90deg, ${light} 0%, ${mid} 50%, ${dark} 100%)`;
};

const UV_GRADIENT = 'linear-gradient(90deg,#22c55e 0%,#f59e0b 25%,#f97316 50%,#ef4444 75%,#8b5cf6 100%)';

export const useWeatherReportColors = (): WeatherReportColorSet => {
  const { colors } = useTheme();
  const { accentColor, pageColor, pageFontColor } = useColorPaletteProvider(false) || {};

  const accent = accentColor || colors.primary6 || '#7139BF';

  return useMemo<WeatherReportColorSet>(() => {
    const cardBg = pageColor || 'mono1';
    const primaryText = pageFontColor || 'mono14';
    const secondaryText = pageFontColor ? chroma(pageFontColor).alpha(0.75).css() : 'mono12';
    const border = pageColor ? (chroma(pageColor).luminance() > 0.5 ? chroma(pageColor).darken(0.5) : chroma(pageColor).brighten(0.7)).alpha(0.28).css() : 'mono3';

    const colorMap = colors as unknown as Record<string, string>;
    const baseCss = typeof cardBg === 'string' && colorMap[cardBg] ? colorMap[cardBg] : (cardBg as string);
    const base = chroma(baseCss);
    const isLightPage = base.luminance() > 0.75;
    const elevated = isLightPage ? base : base.brighten(0.5);
    const pillBg = isLightPage ? 'white' : elevated.alpha(0.9).css();
    const pillBorder = (isLightPage ? base.darken(1) : base.brighten(0.8)).alpha(0.18).css();

    const tokens = {
      cardBg,
      border,
      textPrimary: primaryText,
      textSecondary: secondaryText,
      icon: primaryText,
      pillBg,
      pillBorder,
    } as WeatherReportColorSet['tokens'];

    const gradients = {
      hero: () => `linear-gradient(135deg, ${chroma(colors.mono1).alpha(0.96).css()} 0%, ${chroma(colors.mono2).alpha(0.96).css()} 100%)`,
      precip: makeTrackGradient(accent),
      humidity: makeTrackGradient(accent),
      wind: makeWindGradient(accent),
      uv: UV_GRADIENT,
      temperature: buildTemperatureGradient,
    } as WeatherReportColorSet['gradients'];

    return { accent, tokens, gradients };
  }, [accent, colors, pageColor, pageFontColor]);
};
